import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { PirBoxProductSummary } from '../../model/pirbox/pirbox-product-summary';
import {
  downloadWalletFile,
  getPirProductGraphDetails,
  pirboxActions,
  PirBoxState,
  retrievePirProductDetail,
  retrievePirProductSummary,
} from '../../store';
import { useAppSelector } from '../redux';
import { DownloaderMediaType, FileDownloadedType } from '../../model';
import { PirBoxProductsDetails } from '../../model/pirbox/pir-box-product-detail';
import { YearlyPirIndicatorsBean } from '../../api/generated/objectway-zenith';
import { PersonaTipoUtenteEnum } from '../../api';

export interface PirBoxPageLogic {
  positions: Map<string, PirBoxProductsDetails[]>;
  productsSummary: PirBoxProductSummary[];
  hasOrdinary: boolean;
  hasAlternative: boolean;
  onDownloadFile: (
    mandate: string,
    type: FileDownloadedType,
    mediaType: DownloaderMediaType
  ) => void;
  onShowGraph: (mandate: string) => void;
  graphRenderEnabled: boolean;
  graphDetails: YearlyPirIndicatorsBean;
  onPrevious: () => void;
}

export interface PirBoxData {
  positions: Map<string, PirBoxProductsDetails[]>;
  productsSummary: PirBoxProductSummary[];
  hasOrdinary: boolean;
  hasAlternative: boolean;
}

export const usePirBoxData = (): PirBoxData => {
  const { positions, productsSummary, hasOrdinary, hasAlternative } =
    useAppSelector<PirBoxState>((state) => state.features.pirbox);

  return {
    positions,
    productsSummary,
    hasOrdinary,
    hasAlternative,
  };
};

export const usePirBoxPageLogic = (): PirBoxPageLogic => {
  const {
    positions,
    productsSummary,
    hasOrdinary,
    hasAlternative,
    graphDetails,
  } = useAppSelector<PirBoxState>((state) => state.features.pirbox);

  const userCode = useAppSelector((state) => state.data?.user?.userCode);
  const userType = useAppSelector((state) => state.data?.user?.userType);
  const isProspect = useAppSelector((state) => state.data?.user?.isProspect);

  const dispatch = useDispatch();
  useEffect(() => {
    if (isProspect === false && userCode?.length > 0) {
      if (userType === PersonaTipoUtenteEnum.Pf) {
        dispatch(retrievePirProductSummary(userCode));
      }
    }
  }, [dispatch, userCode, userType, isProspect]);

  useEffect(() => {
    if ((isProspect === false && productsSummary) !== undefined) {
      productsSummary.forEach((summary) => {
        dispatch(retrievePirProductDetail(summary.pirCode));
      });
    }
  }, [productsSummary, isProspect]);

  const onDownloadFile = useCallback(
    (
      mandate: string,
      type: FileDownloadedType,
      mediaType: DownloaderMediaType
    ) => {
      dispatch(pirboxActions.setMandate(mandate));
      dispatch(downloadWalletFile({ type, mediaType }));
    },
    [dispatch]
  );

  const onShowGraph = useCallback(
    (mandate: string) => {
      dispatch(pirboxActions.setMandate(mandate));
      dispatch(getPirProductGraphDetails(mandate));
    },
    [dispatch]
  );

  const graphRenderEnabled = useMemo(() => {
    return graphDetails !== undefined;
  }, [graphDetails]);

  const onPrevious = useCallback(() => {
    dispatch(pirboxActions.resetProductDetails());
  }, [dispatch]);

  return {
    positions,
    productsSummary,
    hasOrdinary,
    hasAlternative,
    onDownloadFile,
    onShowGraph,
    graphRenderEnabled,
    graphDetails,
    onPrevious,
  };
};
